import {
  newId,
  type Board,
  type BoardAudioNode,
  type BoardEdge,
  type BoardImageNode,
  type BoardNode,
  type BoardVideoNode,
} from './canvasBoard';

export const CANVAS_CLIPBOARD_MARKER = 'seu.video-canvas-clipboard:v1';
const STORAGE_KEY = 'seu.video.canvas.clipboard.v1';

export type CanvasClipboardPayload = {
  v: 1;
  copiedAt: number;
  nodes: BoardNode[];
  edges: BoardEdge[];
};

let pasteSerial = 0;

function isClipboardPayload(raw: unknown): raw is CanvasClipboardPayload {
  if (!raw || typeof raw !== 'object') return false;
  const p = raw as CanvasClipboardPayload;
  return p.v === 1 && Array.isArray(p.nodes) && Array.isArray(p.edges);
}

function parseClipboardText(text: string): CanvasClipboardPayload | null {
  const trimmed = text.trim();
  if (!trimmed.startsWith(CANVAS_CLIPBOARD_MARKER)) return null;
  try {
    const json = trimmed.slice(CANVAS_CLIPBOARD_MARKER.length).trim();
    const parsed = JSON.parse(json) as unknown;
    return isClipboardPayload(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function serializeClipboard(payload: CanvasClipboardPayload): string {
  return `${CANVAS_CLIPBOARD_MARKER}\n${JSON.stringify(payload)}`;
}

/** Nodes + arrows that travel with a selection (edges need both ends selected). */
export function snapshotCanvasSelection(
  board: Board,
  selectedIds: string[],
  selectedEdgeId?: string | null,
): CanvasClipboardPayload | null {
  const ids = new Set(selectedIds.filter((id) => board.nodes.some((n) => n.id === id)));
  if (selectedEdgeId) {
    const edge = board.edges.find((e) => e.id === selectedEdgeId);
    if (edge) {
      ids.add(edge.from);
      ids.add(edge.to);
    }
  }
  if (!ids.size) return null;
  const nodes = board.nodes.filter((n) => ids.has(n.id)).map(sanitizeCopiedNode);
  if (!nodes.length) return null;
  const kept = new Set(nodes.map((n) => n.id));
  const edges = board.edges.filter((e) => kept.has(e.from) && kept.has(e.to));
  return { v: 1, copiedAt: Date.now(), nodes, edges };
}

function sanitizeCopiedNode(node: BoardNode): BoardNode {
  if (node.kind === 'image') {
    const ready = Boolean(node.phase.imageUrl);
    return {
      ...node,
      phase: {
        ...node.phase,
        status: ready ? 'ready' : 'pending',
        jobId: undefined,
        error: undefined,
      },
    };
  }
  if (node.kind === 'audio') {
    const ready = Boolean(node.audioUrl);
    return {
      ...node,
      status: ready ? 'ready' : 'idle',
      progress: undefined,
      error: undefined,
    };
  }
  if (node.kind === 'comment') return { ...node };
  const ready = Boolean(node.videoUrl || node.clipUrl);
  return {
    ...node,
    status: ready ? 'ready' : 'idle',
    progress: undefined,
    error: undefined,
    partJobIds: undefined,
    remixJobId: undefined,
  };
}

/** Persist for paste in this or another project (same browser origin). */
export function writeCanvasClipboard(payload: CanvasClipboardPayload): void {
  pasteSerial = 0;
  const text = serializeClipboard(payload);
  try {
    localStorage.setItem(STORAGE_KEY, text);
  } catch {
    /* quota / private mode */
  }
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    void navigator.clipboard.writeText(text).catch(() => undefined);
  }
}

export function readCanvasClipboardFromStorage(): CanvasClipboardPayload | null {
  try {
    const text = localStorage.getItem(STORAGE_KEY);
    return text ? parseClipboardText(text) : null;
  } catch {
    return null;
  }
}

export function readCanvasClipboardFromText(text: string | null | undefined): CanvasClipboardPayload | null {
  if (!text) return null;
  return parseClipboardText(text);
}

export async function readCanvasClipboard(): Promise<CanvasClipboardPayload | null> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.readText) {
    try {
      const fromOs = readCanvasClipboardFromText(await navigator.clipboard.readText());
      if (fromOs) return fromOs;
    } catch {
      /* permission denied — fall back to localStorage */
    }
  }
  return readCanvasClipboardFromStorage();
}

function remapNode(
  node: BoardNode,
  idMap: Map<string, string>,
  groupMap: Map<string, string>,
): BoardNode {
  const id = idMap.get(node.id) || node.id;
  if (node.kind === 'image') {
    const next: BoardImageNode = {
      ...node,
      id,
      phase: { ...node.phase, id },
      versions: node.versions?.map((v) => ({ ...v, id: newId('iver') })),
    };
    return next;
  }
  if (node.kind === 'audio') {
    const next: BoardAudioNode = {
      ...node,
      id,
      sourceAudioId: node.sourceAudioId ? idMap.get(node.sourceAudioId) : undefined,
      versions: node.versions?.map((v) => ({ ...v, id: newId('aver') })),
    };
    return next;
  }
  if (node.kind === 'comment') {
    return { ...node, id };
  }
  const flowGroup = node.flowGroup
    ? (groupMap.get(node.flowGroup) || (() => {
      const g = newId('flows');
      groupMap.set(node.flowGroup!, g);
      return g;
    })())
    : undefined;
  const joinOrder = node.joinOrder
    ?.map((jid) => idMap.get(jid))
    .filter((jid): jid is string => Boolean(jid));
  const next: BoardVideoNode = {
    ...node,
    id,
    flowGroup,
    joinOrder: joinOrder?.length ? joinOrder : undefined,
    versions: node.versions?.map((v) => ({ ...v, id: newId('vver') })),
  };
  return next;
}

/**
 * Paste a clipboard snapshot onto the board.
 * New ids for every node/edge; arrows keep topology; selection lands near `at`.
 */
export function pasteCanvasClipboard(
  board: Board,
  payload: CanvasClipboardPayload,
  at: { x: number; y: number },
): { board: Board; pastedIds: string[] } {
  if (!payload.nodes.length) return { board, pastedIds: [] };
  const idMap = new Map<string, string>();
  const groupMap = new Map<string, string>();
  for (const node of payload.nodes) {
    const prefix = node.kind === 'image' ? 'img'
      : node.kind === 'video' ? 'video'
        : node.kind === 'audio' ? 'audio'
          : 'comment';
    idMap.set(node.id, newId(prefix));
  }

  const xs = payload.nodes.map((n) => n.x);
  const ys = payload.nodes.map((n) => n.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const nudge = 24 * (pasteSerial++ % 8);
  const dx = at.x - minX + nudge;
  const dy = at.y - minY + nudge;

  const nodes = payload.nodes.map((node) => {
    const remapped = remapNode(node, idMap, groupMap);
    return { ...remapped, x: node.x + dx, y: node.y + dy };
  });
  const edges: BoardEdge[] = [];
  for (const edge of payload.edges) {
    const from = idMap.get(edge.from);
    const to = idMap.get(edge.to);
    if (!from || !to) continue;
    const next: BoardEdge = { id: newId('edge'), from, to };
    if (edge.role) next.role = edge.role;
    edges.push(next);
  }

  return {
    board: {
      ...board,
      nodes: [...board.nodes, ...nodes],
      edges: [...board.edges, ...edges],
    },
    pastedIds: nodes.map((n) => n.id),
  };
}
